"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CustomWaitlist from "@/components/CustomWaitlist";
import { Check, Crown, Mail, FileText } from "lucide-react";

export default function WaitlistPageClient() {
  return (
    <div className="py-8 px-4 max-w-2xl mx-auto">
      {/* 헤더 */}
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">
          2026년 1회 실기 이론 요약 PDF
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          출시 알림을 등록하고 가장 먼저 소식을 받아보세요
        </p>
      </div>

      {/* Complete 이용권 추천 섹션 */}
      <Card className="mb-8 border-amber-500/50 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2 mb-2">
            <Crown className="h-5 w-5 text-amber-500" />
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 rounded-full">
              추천
            </span>
          </div>
          <CardTitle className="text-lg sm:text-xl text-center">
            Complete 이용권으로 저렴하게 요약 PDF와 프리미엄 기능을 사용하세요
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 혜택 리스트 */}
          <div className="space-y-2 text-center">
            {/* 요약 PDF - 맨 위, 붉은색 강조 */}
            <div className="flex items-center justify-center gap-2">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-base font-bold text-red-600 dark:text-red-400">
                요약 PDF (이론 + 코딩) 포함
              </span>
            </div>
            {/* 광고 제거 - 강조 */}
            <div className="flex items-center justify-center gap-2">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-sm">
                <span className="font-bold text-amber-600 dark:text-amber-400">광고 제거</span> + 목차/필기 기능
              </span>
            </div>
            {/* 오답 노트 - 강조 */}
            <div className="flex items-center justify-center gap-2">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-sm">
                문제 풀이 이력 + <span className="font-bold text-amber-600 dark:text-amber-400">오답 노트</span>
              </span>
            </div>
            {/* 나머지 항목들 */}
            <div className="flex items-center justify-center gap-2">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-sm">문제 난이도 조절 (보기 비활성화)</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-sm">2회 연속 정답시 보기 복사 가능</span>
            </div>
          </div>

          {/* 중요 안내 */}
          <div className="bg-white/70 dark:bg-gray-900/50 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-2">
              <FileText className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                Complete 이용권에는 이미 출시된{" "}
                <Link 
                  href="/summary-pdf/25-3-compressed" 
                  className="text-amber-600 dark:text-amber-400 font-medium underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300"
                >
                  25년 3회 이론 요약 PDF
                </Link>
                가 포함되어 있습니다
              </p>
            </div>
            <div className="flex items-start gap-2">
              <FileText className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                26년 1회 PDF 출시 전까지{" "}
                <Link 
                  href="/summary-pdf/25-3-compressed" 
                  className="text-amber-600 dark:text-amber-400 font-medium underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300"
                >
                  25년 3회 이론 요약 PDF
                </Link>
                로 먼저 공부하세요
              </p>
            </div>
            <div className="flex items-start gap-2">
              <Mail className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                Complete 이용권 구매자에게는 26년 1회 PDF 출시 시{" "}
                <span className="font-medium text-green-600 dark:text-green-400">
                  자동으로 알람 메일
                </span>
                이 발송됩니다
              </p>
            </div>
            <p className="text-xs text-muted-foreground pl-6">
              별도 대기자 등록 없이도 출시 알림을 받을 수 있습니다
            </p>
          </div>

          {/* Complete 이용권 구매 버튼 */}
          <Button asChild className="w-full bg-amber-500 hover:bg-amber-600 text-white">
            <Link href="/membership">
              <Crown className="h-4 w-4 mr-2" />
              Complete 이용권 구매하기
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* 구분선 */}
      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200 dark:border-gray-700" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-background text-muted-foreground">
            또는 출시 알림만 등록하기
          </span>
        </div>
      </div>

      {/* CustomWaitlist 컴포넌트 */}
      <CustomWaitlist waitlistId="26-1-compressed-pdf" />
    </div>
  );
}

